/**
 * Pure helpers + constants used across the Lane Management page and its
 * extracted sub-components. No React, no state, no side effects — anything
 * here can be safely imported from anywhere.
 */

export const LANE_COLORS = {
  '1': { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', light: 'bg-blue-50' },
  '2': { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700', light: 'bg-green-50' },
  '3': { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700', light: 'bg-purple-50' },
  '4': { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700', light: 'bg-orange-50' },
  '5': { bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-700', light: 'bg-pink-50' },
  '6': { bg: 'bg-cyan-100', border: 'border-cyan-300', text: 'text-cyan-700', light: 'bg-cyan-50' },
  '7': { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-700', light: 'bg-yellow-50' },
  '8': { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-700', light: 'bg-red-50' },
};

export const DEFAULT_LANE_COLOR = {
  bg: 'bg-slate-100',
  border: 'border-slate-300',
  text: 'text-slate-700',
  light: 'bg-slate-50',
};

export const getLaneColor = (laneId) => LANE_COLORS[laneId] || DEFAULT_LANE_COLOR;

/**
 * Color palette per booking duration (minutes). Coaching/class blocks are
 * always red (rendered separately as `status.type === 'blocked'`), so this
 * map only governs customer-booked lane tiles. Each entry follows the same
 * `{ bg, border, text, light }` shape as LANE_COLORS so the calendar tile
 * code can swap palettes in one line.
 */
export const DURATION_COLORS = {
  30:  { bg: 'bg-sky-100',     border: 'border-sky-300',     text: 'text-sky-700',     light: 'bg-sky-50',     label: '30 min'  },
  60:  { bg: 'bg-emerald-100', border: 'border-emerald-300', text: 'text-emerald-700', light: 'bg-emerald-50', label: '1 hour'  },
  90:  { bg: 'bg-violet-100',  border: 'border-violet-300',  text: 'text-violet-700',  light: 'bg-violet-50',  label: '1.5 hr'  },
  120: { bg: 'bg-amber-100',   border: 'border-amber-300',   text: 'text-amber-700',   light: 'bg-amber-50',   label: '2 hr'    },
};

export const DEFAULT_DURATION_COLOR = {
  bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-700', light: 'bg-slate-50', label: 'Other',
};

/** Pick the right duration palette for a booking — falls back to slate. */
export const getDurationColor = (duration) =>
  DURATION_COLORS[Number(duration)] || DEFAULT_DURATION_COLOR;

// Generate time slots (6 AM → 11 PM by default, 30-min intervals).
export const generateTimeSlots = (startHour = 6, endHour = 23, interval = 60) => {
  const slots = [];
  for (let hour = startHour; hour < endHour; hour++) {
    const h = hour.toString().padStart(2, '0');
    slots.push(`${h}:00`);
    if (interval === 30) {
      slots.push(`${h}:30`);
    }
  }
  return slots;
};

export const TIME_SLOTS = generateTimeSlots(6, 23, 30);

export const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

export const formatTime = (time24) => {
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

// Turn "YYYY-MM-DD" into a local-TZ Date (avoids UTC off-by-one issues).
export const parseLocalDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const toLocalDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const timeStringToMinutes = (t) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};
